/**
 * Reiter "Bestellungen" (Migration 0070, Recht "orders.create", ab Werk Rolle
 * Member): eine Bestellung aus Positionen zusammenstellen - je Zeile ein
 * Artikel aus der Preisliste (live synchron, wird beim Öffnen der Seite und
 * bei jedem Fokus eines Dropdowns neu geladen), eine Menge und die
 * Zeilensumme; unten die Gesamtsumme. Preise/Summen werden serverseitig
 * nochmals aus der Preisliste berechnet (siehe backend/src/routes/orders.ts) -
 * die Anzeige hier dient nur der Übersicht.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../../api/client";
import { Card } from "../../components/ui/Card";
import { Badge } from "../../components/ui/Badge";
import { Spinner } from "../../components/ui/Spinner";
import { formatDateTime } from "../../utils/formatDate";
import type { Order, OrderableItem } from "../../types";

export const formatPrice = (price: number) =>
  `${price.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`;

interface Line { key: number; itemId: string; quantity: string }

let lineKeyCounter = 1;
const newLine = (): Line => ({ key: lineKeyCounter++, itemId: "", quantity: "1" });

export default function OrdersPage() {
  const [items, setItems] = useState<OrderableItem[]>([]);
  const [lines, setLines] = useState<Line[]>([newLine()]);
  const [myOrders, setMyOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadItems = useCallback(async () => {
    try {
      setItems(await api.get<OrderableItem[]>("/api/orders/items"));
    } catch {
      // Liste bleibt auf dem letzten Stand
    }
  }, []);

  const loadMyOrders = useCallback(async () => {
    setMyOrders(await api.get<Order[]>("/api/orders/mine"));
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await Promise.all([loadItems(), loadMyOrders()]);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : "Laden fehlgeschlagen.");
      } finally {
        setLoading(false);
      }
    })();
  }, [loadItems, loadMyOrders]);

  const itemsById = useMemo(() => new Map(items.map((i) => [String(i.id), i])), [items]);
  const grouped = useMemo(() => {
    const map = new Map<string, OrderableItem[]>();
    for (const i of items) {
      const list = map.get(i.category_label) ?? [];
      list.push(i);
      map.set(i.category_label, list);
    }
    return [...map.entries()];
  }, [items]);

  const parsedQty = (l: Line) => {
    const n = Number(l.quantity);
    return Number.isInteger(n) && n >= 1 && n <= 999 ? n : 0;
  };
  const lineTotal = (l: Line) => {
    const item = itemsById.get(l.itemId);
    return item ? Math.round(item.price * parsedQty(l) * 100) / 100 : 0;
  };
  const validLines = lines.filter((l) => itemsById.has(l.itemId) && parsedQty(l) > 0);
  const total = Math.round(lines.reduce((sum, l) => sum + lineTotal(l), 0) * 100) / 100;

  function updateLine(key: number, patch: Partial<Line>) {
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }

  async function handleSubmit() {
    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      await api.post("/api/orders", {
        items: validLines.map((l) => ({ priceItemId: Number(l.itemId), quantity: parsedQty(l) })),
      });
      setSuccess("Bestellung wurde abgeschickt.");
      setLines([newLine()]);
      await loadMyOrders();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Bestellung konnte nicht abgeschickt werden.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-100">Bestellungen</h1>
        <p className="text-sm text-gray-500 mt-1">Artikel aus der Preisliste mit Menge auswählen und die Bestellung abschicken.</p>
      </div>

      {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2">{error}</div>}
      {success && <div className="bg-success-muted text-success text-sm rounded-lg px-3 py-2">{success}</div>}

      {loading ? (
        <Spinner />
      ) : (
        <>
          <Card className="p-4 space-y-3">
            <h2 className="font-display text-base font-semibold text-gray-100 uppercase tracking-wide">Neue Bestellung</h2>

            <div className="hidden md:grid grid-cols-[1fr_6rem_8rem_2rem] gap-3 text-xs text-gray-500 uppercase tracking-wide px-1">
              <span>Artikel</span><span>Menge</span><span className="text-right">Summe</span><span />
            </div>

            {lines.map((l) => {
              const item = itemsById.get(l.itemId);
              return (
                <div key={l.key} className="grid grid-cols-1 md:grid-cols-[1fr_6rem_8rem_2rem] gap-2 md:gap-3 items-center bg-surface-raised rounded-lg p-2 md:p-1 md:bg-transparent">
                  <select
                    className="input text-sm"
                    value={l.itemId}
                    onFocus={loadItems}
                    onChange={(e) => updateLine(l.key, { itemId: e.target.value })}
                    aria-label="Artikel"
                  >
                    <option value="">— Artikel auswählen —</option>
                    {grouped.map(([label, list]) => (
                      <optgroup key={label} label={label}>
                        {list.map((i) => <option key={i.id} value={i.id}>{i.name} — {formatPrice(i.price)}</option>)}
                      </optgroup>
                    ))}
                  </select>
                  <input
                    className="input text-sm"
                    type="number"
                    min={1}
                    max={999}
                    step={1}
                    value={l.quantity}
                    onChange={(e) => updateLine(l.key, { quantity: e.target.value })}
                    aria-label="Menge"
                  />
                  <p className="text-sm text-gray-100 font-medium md:text-right whitespace-nowrap">
                    {item ? formatPrice(lineTotal(l)) : "—"}
                  </p>
                  <button
                    className="text-gray-500 hover:text-danger text-lg leading-none justify-self-end disabled:opacity-30"
                    onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}
                    disabled={lines.length === 1}
                    aria-label="Position entfernen"
                    title="Position entfernen"
                  >
                    ×
                  </button>
                </div>
              );
            })}

            <button className="btn-secondary text-sm" onClick={() => setLines((ls) => [...ls, newLine()])} disabled={lines.length >= 50}>
              + Weitere Position
            </button>

            <div className="border-t border-surface-border pt-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Gesamtsumme</p>
                <p className="text-2xl font-semibold text-gray-100">{formatPrice(total)}</p>
                <p className="text-xs text-gray-500">{validLines.length} {validLines.length === 1 ? "Position" : "Positionen"}</p>
              </div>
              <button className="btn-primary" onClick={handleSubmit} disabled={submitting || validLines.length === 0}>
                {submitting ? "Wird abgeschickt…" : "Bestellung abschicken"}
              </button>
            </div>
          </Card>

          <Card className="p-4 space-y-3">
            <h2 className="font-display text-base font-semibold text-gray-100 uppercase tracking-wide">Meine offenen Bestellungen</h2>
            <p className="text-xs text-gray-500 -mt-1">Abgeschlossene Bestellungen werden automatisch gelöscht und verschwinden hier.</p>
            {myOrders.length === 0 ? (
              <p className="text-sm text-gray-500">Keine offenen Bestellungen.</p>
            ) : (
              <div className="space-y-2">
                {myOrders.map((o) => (
                  <div key={o.id} className="bg-surface-raised rounded-lg px-3 py-2.5">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm text-gray-100 font-medium">
                        Bestellung #{o.id} <span className="text-gray-500 font-normal">· {formatDateTime(o.created_at)}</span>
                      </p>
                      <div className="flex items-center gap-3">
                        <Badge tone="warning">In Bearbeitung</Badge>
                        <span className="text-sm text-gray-100 font-semibold whitespace-nowrap">{formatPrice(o.total)}</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 mt-1 break-words">
                      {o.items.map((i) => `${i.quantity}× ${i.item_name}`).join(", ")}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
